/**
 * Pagination.jsx
 *
 * Ortak sayfalama bileşeni — CRM liste ekranlarında (Potansiyeller, Son
 * Aramalar, Yaklaşan Hatırlatmalar, Data Detay Geçmiş, Hatırlatmalar tablosu,
 * Ödemesi Gelenler) TEK yerden kullanılır (bkz. proje-yoneticisi görev
 * talimatı, 2026-08-13 — İş A). Kopyala-yapıştır YERİNE burası import edilir.
 *
 * Sözleşme: backend limit/offset/total üçlüsünü döner (bkz.
 * routes/danisman_cockpit.py). Bu bileşen SAF (state tutmaz) — offset/limit
 * state'i çağıran bileşende yaşar, `onPageChange`/`onLimitChange` ile
 * bildirilir. Böylece "filtre değişince offset 0'a dönsün", "kayıt sonrası
 * refetch mevcut sayfayı korusun" gibi kararlar çağıran bileşende kalır.
 *
 * Davranış:
 *  - total <= limit VE offset === 0 ise HİÇBİR ŞEY render edilmez (gereksiz
 *    gürültü — tek sayfaya sığan liste için sayfalama arayüzü anlamsız).
 *  - Önceki/Sonraki: gerçek <button>, ilk/son sayfada disabled.
 *  - "X–Y / Z" bilgisi aria-live="polite" ile duyurulur (sayfa değişince
 *    ekran okuyucu güncellemeyi yakalar) — odak PREV/NEXT butonlarında
 *    kalır, bileşen remount OLMADIĞI için kaybolmaz.
 *  - Sayfa boyutu seçici `onLimitChange` verilmişse gösterilir.
 */
import { ChevronLeft, ChevronRight } from 'lucide-react';

export default function Pagination({
  total,
  limit,
  offset,
  onPageChange,
  onLimitChange,
  pageSizeOptions = [25, 50, 100],
  loading = false,
  tr,
  className = '',
}) {
  if (total <= limit && offset === 0) return null;

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.floor(offset / limit) + 1;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);

  const goPrev = () => {
    if (offset === 0 || loading) return;
    onPageChange(Math.max(0, offset - limit));
  };
  const goNext = () => {
    if (currentPage >= totalPages || loading) return;
    onPageChange(offset + limit);
  };
  const changeLimit = (newLimit) => {
    if (newLimit === limit || loading) return;
    onLimitChange(newLimit);
  };

  return (
    <div
      className={`flex items-center justify-between p-4 border-t border-[var(--border-light)] flex-wrap gap-3 ${className}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        {onLimitChange && (
          <>
            <span className="text-xs text-[var(--text-muted)] font-semibold">
              {tr('Sayfa başı:', 'Per page:')}
            </span>
            {pageSizeOptions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => changeLimit(s)}
                disabled={loading}
                aria-pressed={limit === s}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold border-2 disabled:opacity-50 ${
                  limit === s
                    ? 'bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]'
                    : 'bg-white border-[var(--border-light)] text-[var(--text-muted)]'
                }`}
              >
                {s}
              </button>
            ))}
          </>
        )}
        <span
          className="text-sm text-[var(--text-muted)] font-semibold ml-1"
          aria-live="polite"
        >
          {tr(`${from}–${to} / ${total} kayıt`, `${from}–${to} of ${total}`)}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={goPrev}
          disabled={offset === 0 || loading}
          aria-label={tr('Önceki sayfa', 'Previous page')}
          className="p-2 rounded-lg border-2 border-[var(--border-light)] disabled:opacity-40"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="px-3 py-2 text-sm font-bold">
          {currentPage} / {totalPages}
        </span>
        <button
          type="button"
          onClick={goNext}
          disabled={currentPage >= totalPages || loading}
          aria-label={tr('Sonraki sayfa', 'Next page')}
          className="p-2 rounded-lg border-2 border-[var(--border-light)] disabled:opacity-40"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
